// @ƒlow
import React, { Component } from 'react';

import i18n from 'helper/i18n';

import Icon from 'components/atoms/Icon';
import RecipeTooltip from 'components/molecules/Recipe/fragments/Tooltip';

import { RecipeCalculatorBoundariesType } from 'store/recipes';
import { RecipeType } from 'types/Recipe';

type RecipePortionCalculatorProps = {
	selectedPortion?: any;
	externalId: string;
	recipe: RecipeType;
	directionBoundaries: RecipeCalculatorBoundariesType;
	variantList: number[];
	doPersistPortionSelection: (externalId: string, portion: number) => void;
};

type RecipePortionCalculatorState = {
	tooltipsVisible: {
		decrement: boolean;
		increment: boolean;
	};
};

type RecipeCalculatorStepDirectionType = 'inc' | 'dec';

const RECIPE_CALCULATOR_STEP_DIRECTIONS = {
	INCREMENT: 'inc',
	DECREMENT: 'dec',
};

export class RecipePortionCalculator extends Component<RecipePortionCalculatorProps, RecipePortionCalculatorState> {
	public static displayName = 'RecipePortionCalculator';

	public state: RecipePortionCalculatorState = {
		tooltipsVisible: {
			decrement: false,
			increment: false,
		},
	};

	public componentDidUpdate(prevProps: RecipePortionCalculatorProps) {
		if (prevProps.selectedPortion !== this.props.selectedPortion) {
			this.setState({
				tooltipsVisible: {
					decrement: false,
					increment: false,
				},
			});
		}
	}

	public getTooltipVisibilityHandler(dir: RecipeCalculatorStepDirectionType, dismiss: boolean = false): Function {
		const tooltipState: { increment: any; decrement: any } = {} as any;

		if (dir === RECIPE_CALCULATOR_STEP_DIRECTIONS.INCREMENT) {
			tooltipState.increment = !dismiss;
		} else if (dir === RECIPE_CALCULATOR_STEP_DIRECTIONS.DECREMENT) {
			tooltipState.decrement = !dismiss;
		}

		return () => {
			this.setState({
				tooltipsVisible: {
					...this.state.tooltipsVisible,
					...tooltipState,
				},
			});
		};
	}

	public updateSelectedPortionState(
		direction: RecipeCalculatorStepDirectionType = RECIPE_CALCULATOR_STEP_DIRECTIONS.INCREMENT as any
	): void {
		const availableVariants = this.props.variantList;
		const indexOfCurrentVariant = availableVariants.indexOf(this.props.selectedPortion);
		let nextPortionAmount = this.props.selectedPortion;

		switch (direction.toLowerCase()) {
			case RECIPE_CALCULATOR_STEP_DIRECTIONS.INCREMENT:
				nextPortionAmount = availableVariants[indexOfCurrentVariant + 1];
				break;
			case RECIPE_CALCULATOR_STEP_DIRECTIONS.DECREMENT:
				nextPortionAmount = availableVariants[indexOfCurrentVariant - 1];
				break;
			default:
				break;
		}

		this.props.doPersistPortionSelection(this.props.externalId, nextPortionAmount);
	}

	public getClickHandler(dir: RecipeCalculatorStepDirectionType): any {
		const { directionBoundaries } = this.props;
		const { decrement, increment } = directionBoundaries;

		if (
			(decrement === false && dir === RECIPE_CALCULATOR_STEP_DIRECTIONS.DECREMENT) ||
			(increment === false && dir === RECIPE_CALCULATOR_STEP_DIRECTIONS.INCREMENT)
		) {
			return this.getTooltipVisibilityHandler(dir);
		}

		return () => this.updateSelectedPortionState(dir);
	}

	public render() {
		const { recipe, directionBoundaries, variantList, selectedPortion } = this.props;
		const { variants } = recipe;
		const tooltipsVisible = this.state.tooltipsVisible;

		if (!variantList || variantList.length <= 1) {
			// too less variants to render
			return null;
		}

		return (
			<div className="m-recipe-calculator">
				<div className="m-recipe-calculator__btn-wrap">
					{tooltipsVisible.decrement && (
						<div className={'m-recipe-calculator__tooltip'}>
							<RecipeTooltip
								text={i18n('react.impulsCoach.components.recipe.calculator.tooltip.decrement.text')}
								visible={tooltipsVisible.decrement}
								onClose={this.getTooltipVisibilityHandler(
									RECIPE_CALCULATOR_STEP_DIRECTIONS.DECREMENT as any,
									true
								)}
							/>
						</div>
					)}
					<button
						onClick={this.getClickHandler(RECIPE_CALCULATOR_STEP_DIRECTIONS.DECREMENT as any)}
						className={`m-recipe-calculator__btn${
							directionBoundaries.decrement === false ? ' state-m-recipe-calculator__btn--disabled' : ''
						}`}
					>
						<Icon iconName="minus" />
					</button>
				</div>
				{variants && variants[selectedPortion] && (
					<span className="m-recipe-calculator__text">{variants![selectedPortion]!.base_size!.text}</span>
				)}
				<div className="m-recipe-calculator__btn-wrap">
					{tooltipsVisible.increment && (
						<div className={'m-recipe-calculator__tooltip m-recipe-calculator__tooltip--right'}>
							<RecipeTooltip
								text={i18n('react.impulsCoach.components.recipe.calculator.tooltip.increment.text')}
								modifier="right"
								visible={tooltipsVisible.increment}
								onClose={this.getTooltipVisibilityHandler(
									RECIPE_CALCULATOR_STEP_DIRECTIONS.INCREMENT as any,
									true
								)}
							/>
						</div>
					)}
					<button
						onClick={this.getClickHandler(RECIPE_CALCULATOR_STEP_DIRECTIONS.INCREMENT as any)}
						className={`m-recipe-calculator__btn${
							directionBoundaries.increment === false ? ' state-m-recipe-calculator__btn--disabled' : ''
						}`}
					>
						<Icon iconName="plus" />
					</button>
				</div>
			</div>
		);
	}
}

export default RecipePortionCalculator;
